import { apiService } from '@/feature/api/axios';
import { handleApiError } from '@/shared/utils/errorUtils';

import type { IOrgProjectsQuery, IOrgProjectsResponse } from '../types/orgProject';
import type {
  IOrganization,
  IOrganizationsListResponse,
  ICreateOrganizationRequest,
} from '../types/organization';

const emptyProjectsResponse = (): IOrgProjectsResponse => ({
  projects: [],
  pagination: {
    total_count: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_previous: false,
    fetch_all: false,
  },
});

export const organizationService = {
  getOrganizations: async (): Promise<IOrganization[]> => {
    try {
      const api = apiService();
      // API returns either an array directly or an object with results/items
      const response = await api.get<IOrganizationsListResponse | IOrganization[]>(
        '/api/ai-automation-admin/organizations/'
      );

      // Handle both response formats
      if (Array.isArray(response)) {
        return response;
      }

      // If response has results or items array
      if ('results' in response && Array.isArray(response.results)) {
        return response.results;
      }

      if ('items' in response && Array.isArray(response.items)) {
        return response.items;
      }

      if ('organizations' in response && Array.isArray(response.organizations)) {
        return response.organizations;
      }

      // If response is a single organization object, wrap it in array
      if (response && typeof response === 'object' && 'id' in response) {
        return [response as IOrganization];
      }

      return [];
    } catch (error) {
      handleApiError(error, 'Failed to fetch organizations. Please try again.');
      return [];
    }
  },

  resendActivationMail: async (
    organizationId: string,
    payload: { email: string; admin_name: string }
  ): Promise<boolean> => {
    try {
      const api = apiService();
      await api.create(
        `/api/ai-automation-admin/organizations/${organizationId}/resend-activation-mail/`,
        payload
      );
      return true;
    } catch (error) {
      handleApiError(error, 'Failed to resend activation email. Please try again.');
      return false;
    }
  },

  getOrganizationById: async (id: string): Promise<IOrganization | null> => {
    try {
      const api = apiService();
      const response = await api.get<IOrganization>(
        `/api/ai-automation-admin/organizations/${id}/`
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to get organization details. Please try again.');
      return null;
    }
  },

  getOrganizationProjects: async (
    orgId: string,
    query: IOrgProjectsQuery = {}
  ): Promise<IOrgProjectsResponse> => {
    try {
      const api = apiService();
      const params: Record<string, unknown> = {};
      if (query.search) {
        params.search = query.search;
      }
      if (query.page !== undefined) {
        params.page = query.page;
      }
      if (query.page_size !== undefined) {
        params.page_size = query.page_size;
      }
      if (query.sort) {
        params.sort = query.sort;
      }

      const response = await api.get<IOrgProjectsResponse>(
        `/api/ai-automation-admin/organizations/${orgId}/projects/`,
        params
      );

      const projects = Array.isArray(response.projects) ? response.projects : [];
      const pagination = response.pagination;
      const totalCount =
        pagination?.total_count ??
        pagination?.total ??
        pagination?.count ??
        response.count ??
        projects.length;

      return {
        ...response,
        projects,
        pagination: {
          total_count: totalCount,
          limit: pagination?.page_size ?? pagination?.limit ?? query.page_size ?? 10,
          offset: pagination?.offset ?? ((query.page ?? 1) - 1) * (query.page_size ?? 10),
          has_next: pagination?.has_next ?? false,
          has_previous: pagination?.has_previous ?? false,
          fetch_all: pagination?.fetch_all ?? false,
          page: pagination?.page ?? query.page,
          page_size: pagination?.page_size ?? query.page_size,
        },
      };
    } catch (error) {
      handleApiError(error, 'Failed to load organization projects. Please try again.');
      return emptyProjectsResponse();
    }
  },

  createOrganization: async (data: ICreateOrganizationRequest): Promise<IOrganization | null> => {
    try {
      const api = apiService();
      const response = await api.create<ICreateOrganizationRequest, IOrganization>(
        '/api/ai-automation-admin/organizations/',
        data
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to create organization. Please try again.');
      return null;
    }
  },

  updateOrganization: async (
    id: string,
    data: Partial<ICreateOrganizationRequest>
  ): Promise<IOrganization | null> => {
    try {
      const api = apiService();
      const response = await api.update<Partial<ICreateOrganizationRequest>, IOrganization>(
        '/api/ai-automation-admin/organizations/',
        id,
        data
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to update organization. Please try again.');
      return null;
    }
  },

  deleteOrganization: async (id: string, softDelete: boolean = true): Promise<boolean> => {
    try {
      const api = apiService();

      // Try simple DELETE first (no parameters) - most REST APIs use this
      try {
        await api.delete('/api/ai-automation-admin/organizations/', id);
        return true;
      } catch {
        // Try with query parameters
        try {
          await api.delete('/api/ai-automation-admin/organizations/', id, {
            soft_delete: softDelete,
          });
          return true;
        } catch {
          // Try with request body (some APIs require this)
          // eslint-disable-next-line no-useless-catch
          try {
            await api.delete('/api/ai-automation-admin/organizations/', id, undefined, {
              soft_delete: softDelete,
            });
            return true;
          } catch (bodyError: unknown) {
            // All methods failed, throw the last error
            throw bodyError;
          }
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to delete organization. Please try again.');
      return false;
    }
  },
};
